<script setup lang="ts">
import TextButton from '@/components/input/Btn/TextButton.vue';
import { i18n } from '@/i18n';
import SvgLoader from '@/utils/SvgLoader.vue';
import { watch } from 'vue';
import { computed, ref, StyleValue } from 'vue';
import { useForms } from '../composables/useForm';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import InfoModal from '../components/InfoModal.vue';
import ErrorMessage from '../components/ErrorMessage.vue';

const t = i18n.t;

const props = withDefaults(
  defineProps<{
    memo?: string;
    modalCoordinateBase?: 'bottom-left' | 'bottom-right';
    needConfirmDeletion?: boolean;
    needLabelText?: boolean;
  }>(),
  {
    memo: undefined,
    modalCoordinateBase: 'bottom-left',
    needConfirmDeletion: true,
    needLabelText: false,
  }
);

const emits = defineEmits<{
  (e: 'save', value: string): void;
  (e: 'delete'): void;
}>();

const fieldItems = computed((): FieldItem[] => {
  const items: FieldItem[] = [
    {
      name: 'memo',
      rules: [ValidateRule.MemoInvalidChar, ValidateRule.MemoTooMuchLength],
    },
  ];
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(fieldItems.value);

const modalState = ref(false);
const deleteModalState = ref(false);
const coordinateMemoButton = ref({
  x: 0,
  y: 0,
});
const isSaveable = ref(false);
const isEllipsis = ref(false);

fieldState['memo'].setValue(props.memo ?? '');

watch(
  () => props.memo,
  (newValue) => {
    resetForm(fieldItems.value);
    fieldState['memo'].setValue(newValue ?? '');
  }
);

/**
 * メモボタンをクリックした際の挙動
 * @param e - イベント
 */
const onClickMemoButton = (e: Event) => {
  const eventElement = e.target as HTMLElement;
  const parentElement = eventElement.closest('td') || eventElement.parentElement;
  if (!eventElement || !parentElement) return;

  const rect = parentElement.getBoundingClientRect();
  const offsetY = 20;
  const modalHeight = 278;
  const modalWidth = 500; // モーダルの幅を考慮
  const screenWidth = window.innerWidth;
  const edgePadding = 16; // 画面端からの余白
  const edgeWidth = screenWidth - edgePadding;

  // X座標の計算: modalCoordinateBase に応じて左上または右上を基準にする
  let coordX = props.modalCoordinateBase === 'bottom-right' ? rect.right + window.scrollX - modalWidth : rect.left + window.scrollX;

  // モーダルが画面の左端を超えないように調整
  if (coordX < edgePadding) {
    coordX = edgePadding;
  }

  // モーダルが画面の右端を超えないように調整
  if (coordX + modalWidth > edgeWidth) {
    coordX = edgeWidth - modalWidth;
  }

  // Y座標の計算: メモボタンの左下または右下に基準を合わせる
  const coordY =
    rect.top + window.scrollY + offsetY + modalHeight < window.scrollY + window.innerHeight
      ? rect.top + window.scrollY + offsetY
      : window.scrollY + window.innerHeight - modalHeight;

  coordinateMemoButton.value = { x: coordX, y: coordY };
  modalState.value = true;
};

const memoModalStyle = computed((): StyleValue => {
  return {
    top: `${coordinateMemoButton.value.y}px`,
    left: `${coordinateMemoButton.value.x}px`,
  };
});

/**
 * キャンセルボタンクリック時/バックグラウンドクリック時の処理
 */
const onClickClose = () => {
  modalState.value = false;
};

/**
 * 保存ボタンクリック時の処理
 */
const onClickSave = () => {
  emits('save', fieldState['memo'].value.value);
  modalState.value = false;
};

/**
 * 削除ボタンクリック時の処理
 */
const onClickDelete = () => {
  if (!props.needConfirmDeletion) {
    // 削除確認モーダルを表示しない場合は直で削除処理を実行
    applyDelete();
    modalState.value = false;
    return;
  }
  deleteModalState.value = true;
  modalState.value = false;
};

/**
 * 削除確認モーダルで削除を押した際の処理
 */
const applyDelete = () => {
  emits('delete');
  deleteModalState.value = false;
};

/**
 * 入力フォーム変更時の処理
 * @param e - イベント
 */
const onChangeInput = (e: Event) => {
  if (!e.target) return;
  const newValue = (e.target as HTMLTextAreaElement).value;
  fieldState['memo'].setValue(newValue ?? '');
  isSaveable.value = allValidate() && !!newValue.replace('\n', '').replace(/\s+/g, '');
};

const textFieldStyle = computed((): StyleValue => {
  return fieldState['memo'].isError.value ? { borderColor: '#d50000' } : { borderColor: '#e0e0e0' };
});

/**
 * テキストにマウスオーバーしたときに、テキストが省略されているかどうかを判定する
 * @param e - イベント
 */
const mouseoverTextLabel = (e: MouseEvent) => {
  const { target } = e;
  if (!(target instanceof HTMLElement)) return;
  isEllipsis.value = target.offsetWidth < target.scrollWidth;
};
</script>
<template>
  <template v-if="props.memo">
    <div
      v-tooltip.bottom="isEllipsis ? props.memo : ''"
      class="memo__text-button"
      :style="{ fontSize: props.needLabelText ? '14px' : '12px' }"
      @click="onClickMemoButton"
      @mouseover="mouseoverTextLabel"
    >
      <span v-if="props.needLabelText">{{ t('メモ') }}：</span>{{ props.memo }}
    </div>
  </template>
  <template v-else>
    <template v-if="props.needLabelText">
      <button class="memo__button--with-label" :style="{ fontSize: props.needLabelText ? '14px' : '12px' }" @click="onClickMemoButton">
        {{ t('メモ') }}：<SvgLoader name="addCircle" />
      </button>
    </template>
    <template v-else>
      <button class="memo__button" @click="onClickMemoButton">
        <SvgLoader name="addCircle" />
      </button>
    </template>
  </template>
  <Teleport v-if="modalState" to="body">
    <div class="memo__modal__bg" @click="onClickClose" />
    <div class="memo__modal__container" :style="memoModalStyle">
      <div class="memo__modal__inner">
        <div class="memo__modal__title">
          {{ props.memo ? t('メモ') : t('メモの追加') }}
        </div>
        <textarea
          :key="'memo-input'"
          class="memo__modal__text-field"
          :placeholder="t('255文字以内で記入してください')"
          :style="textFieldStyle"
          :value="fieldState['memo'].value.value"
          @input="onChangeInput"
        />
        <div class="memo__modal__info">
          <ErrorMessage :is-error="fieldState['memo'].isError.value" :error-message="fieldState['memo'].errorMessage.value" />
          <div class="memo__modal__info__length">{{ fieldState['memo'].value.value.length }} / 255</div>
        </div>
        <div class="memo__modal__control">
          <div>
            <div v-if="props.memo" class="memo__modal__control__delete" @click="onClickDelete">
              {{ t('このメモを削除') }}
            </div>
          </div>
          <div class="memo__modal__control__inner">
            <TextButton class="memo__modal__control__button" size="btn-sm" button-style="btn-secondary" @click="onClickClose">
              {{ t('キャンセル') }}
            </TextButton>
            <TextButton
              class="memo__modal__control__button"
              size="btn-sm"
              :button-style="!isSaveable || fieldState['memo'].isError.value ? 'btn-disabled' : 'btn-primary'"
              :disabled="!isSaveable || fieldState['memo'].isError.value"
              @click="onClickSave"
            >
              {{ t('保存') }}
            </TextButton>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
  <Teleport v-if="deleteModalState" to="body">
    <InfoModal
      :modal-state="deleteModalState"
      icon="error"
      color-style="error"
      :back-button-label="t('キャンセル')"
      :next-button-label="t('削除')"
      @on-back="
        () => {
          deleteModalState = false;
          modalState = true;
          return;
        }
      "
      @on-next="applyDelete()"
    >
      <template #title>{{ t('メモの削除') }}</template>
      <template #description>
        {{ t('一度削除すると復元することはできません。') }}<br />
        {{ t('本当に削除しますか？') }}
      </template>
    </InfoModal>
  </Teleport>
</template>
<style lang="scss" scoped>
.memo {
  &__text-button {
    width: 100%;

    color: $semanticPrimary;
    text-decoration: underline;
    text-decoration-color: $borderActive;

    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__button {
    &--with-label {
      display: grid;
      grid-template-columns: max-content min-content;
      align-items: center;
      color: $semanticPrimary;
      text-decoration: underline;
      text-decoration-color: $borderActive;
    }
  }
  &__modal {
    &__bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 11;
    }

    &__container {
      position: absolute;
      width: 500px;
      height: 278px;
      max-width: 73%;
      z-index: 12;
      padding: 16px 8px;
      border-radius: 8px;
      background-color: $semanticWhite;
      box-shadow: 0 0 22px 0 #80808066;
    }

    &__inner {
      display: grid;
      gap: 8px;
    }

    &__title {
      font-size: 14px;
      color: $textDefault;
      font-weight: 700;
    }

    &__text-field {
      width: 100%;
      height: 140px;
      padding: 10px 16px 10px 8px;
      resize: none;
      box-sizing: border-box;
      border: solid 1px $borderDefault;
      border-radius: 4px;
      font-size: 12px;
    }

    &__info {
      width: 100%;
      display: flex;
      justify-content: space-between;

      &__error {
        font-size: 12px;
        color: $semanticError;
      }

      &__length {
        font-size: 12px;
      }
    }

    &__control {
      display: grid;
      grid-template-columns: auto auto;
      justify-content: space-between;
      align-items: center;
      margin: 8px 0 0 0;

      &__inner {
        display: flex;
        gap: 4px;
      }

      &__delete {
        color: $semanticError;
        cursor: pointer;
      }

      &__button {
        width: 82px;
        height: 34px;
        padding: 10px 8px;
        font-size: 12px;
      }
    }
  }
}
</style>
